import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class HtmlToMarkdownHelper {
  static convert(html: string): string {
    if (!html) return '';

    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    const markdown = this.convertNode(doc.body);
    // Replace 3 or more consecutive newlines with 2 newlines, and trim
    return markdown.replace(/\n{3,}/g, '\n\n').trim();
  }

  private static convertNode(node: Node): string {
    if (node.nodeType === Node.TEXT_NODE) {
      return node.textContent || '';
    }

    if (node.nodeType !== Node.ELEMENT_NODE) {
      return '';
    }

    const element = node as HTMLElement;
    const tagName = element.tagName.toLowerCase();

    const convertChildren = () => {
      let result = '';
      node.childNodes.forEach((child) => {
        result += this.convertNode(child);
      });
      return result;
    };

    switch (tagName) {
      case 'h1':
        return `\n# ${convertChildren().trim()}\n\n`;
      case 'h2':
        return `\n## ${convertChildren().trim()}\n\n`;
      case 'h3':
        return `\n### ${convertChildren().trim()}\n\n`;
      case 'h4':
        return `\n#### ${convertChildren().trim()}\n\n`;
      case 'h5':
        return `\n##### ${convertChildren().trim()}\n\n`;
      case 'h6':
        return `\n###### ${convertChildren().trim()}\n\n`;
      case 'p': {
        const text = convertChildren().trim();
        return text ? `\n${text}\n\n` : '';
      }
      case 'br':
        return '\n';
      case 'strong':
      case 'b':
        return `**${convertChildren()}**`;
      case 'em':
      case 'i':
        return `*${convertChildren()}*`;
      case 'code': {
        if (element.parentElement?.tagName.toLowerCase() === 'pre') {
          return convertChildren();
        }
        return `\`${convertChildren()}\``;
      }
      case 'pre': {
        const codeElem = element.querySelector('code');
        let lang = '';
        if (codeElem) {
          const classes = Array.from(codeElem.classList);
          for (const cls of classes) {
            const match = cls.match(/^(?:language|lang)-(\w+)$/);
            if (match) {
              lang = match[1];
              break;
            }
          }
        }
        const rawCode = element.textContent || '';
        return `\n\`\`\`${lang}\n${rawCode.trim()}\n\`\`\`\n\n`;
      }
      case 'ul':
        return `\n${convertChildren()}\n`;
      case 'ol':
        return `\n${convertChildren()}\n`;
      case 'li': {
        const parent = element.parentElement;
        const parentTag = parent?.tagName.toLowerCase();
        const content = convertChildren().trim();
        if (parentTag === 'ol') {
          const index = Array.from(parent!.children).indexOf(element) + 1;
          return `${index}. ${content}\n`;
        }
        return `- ${content}\n`;
      }
      case 'a':
        return `[${convertChildren()}](${element.getAttribute('href') || ''})`;
      case 'img':
        return `![${element.getAttribute('alt') || ''}](${element.getAttribute('src') || ''})`;
      case 'div':
      case 'section':
      case 'article':
        return `\n${convertChildren()}\n`;
      default:
        return convertChildren();
    }
  }
}
